// =============================================================================
// features/sales/agenda/AgendaHeader.tsx — Cabeçalho da agenda: título, data
// local, alternância lista/calendário e botão "Novo agendamento" (doc 26
// §6.1).
//
// F35-S24: o botão abria só um toast de placeholder — o formulário
// (`ScheduleAppointmentDialog`) já existe e é usado pelo live chat e pelo
// CRM, mas exige um `leadId` que a Agenda não tem (ela começa do zero, ao
// contrário das outras duas telas). `NewAppointmentFlow` resolve isso com um
// seletor de lead antes de abrir o diálogo reutilizado — ver
// agenda/components/NewAppointmentFlow.tsx e LeadPickerDialog.tsx.
//
// Gate de visibilidade: mesma permissão do resto da agenda de escrita
// (`sales_appointments:write`, ver ScheduleReturnButton no live chat) — sem
// ela, o botão nem aparece.
// =============================================================================

import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useAuth } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';

import { NewAppointmentFlow } from './components/NewAppointmentFlow';
import { formatFullDateOrgTz } from './timezone';

export type AgendaViewMode = 'lista' | 'calendario';

export interface AgendaHeaderProps {
  now: Date;
  viewMode: AgendaViewMode;
  onViewModeChange: (mode: AgendaViewMode) => void;
}

const WRITE_PERMISSION = 'sales_appointments:write';

/**
 * Pura e exportada para teste (sem RTL neste pacote): decide se o botão
 * "Novo agendamento" aparece. Um ator sem a permissão de escrita nunca vê o
 * botão — não é "aparece desabilitado", é ausente do DOM.
 */
export function canShowNewAppointmentButton(hasWritePermission: boolean): boolean {
  return hasWritePermission;
}

export function AgendaHeader({
  now,
  viewMode,
  onViewModeChange,
}: AgendaHeaderProps): React.JSX.Element {
  const { hasPermission } = useAuth();
  const [isFlowOpen, setIsFlowOpen] = React.useState(false);
  const canWrite = canShowNewAppointmentButton(hasPermission(WRITE_PERMISSION));

  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1
          className="font-display font-bold text-ink"
          style={{
            fontSize: 'var(--text-3xl)',
            letterSpacing: '-0.045em',
            lineHeight: 1.05,
            fontVariationSettings: "'opsz' 48",
          }}
        >
          Agenda
        </h1>
        <p className="font-sans mt-1 text-sm text-ink-3">{formatFullDateOrgTz(now)}</p>
      </div>

      <div className="flex items-center gap-3">
        {/* Alternância lista/calendário — doc 26 §6.1: a 1ª entrega pode
            renderizar a lista agrupada por dia (calendário rico é follow-up). */}
        <div
          role="tablist"
          aria-label="Modo de exibição"
          className="inline-flex gap-1 p-1 rounded-md border border-border bg-surface-2"
        >
          {(['lista', 'calendario'] as const).map((mode) => {
            const active = mode === viewMode;
            return (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onViewModeChange(mode)}
                className={cn(
                  'px-3 py-1.5 rounded-sm font-sans text-xs font-semibold min-h-[36px]',
                  'transition-colors duration-fast ease-out',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
                  active ? 'bg-surface-1 text-azul' : 'text-ink-3 hover:text-ink',
                )}
                style={active ? { boxShadow: 'var(--elev-1)' } : undefined}
              >
                {mode === 'lista' ? 'Lista' : 'Calendário'}
              </button>
            );
          })}
        </div>

        {canWrite && (
          <Button variant="primary" onClick={() => setIsFlowOpen(true)}>
            Novo agendamento
          </Button>
        )}
      </div>

      {canWrite && <NewAppointmentFlow open={isFlowOpen} onOpenChange={setIsFlowOpen} />}
    </div>
  );
}
